import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ConfirmationDialogService } from '../../shared-services/confirmation-dialog.service';
import { JsonDrivenSmartTableComponent } from '../../chart-components/json-driven-smart-table/json-driven-smart-table.component';

/** Toolbar select rendered inside the card frame (option contract mirrors global-dropdown). */
export interface TextareaToolbarSelect {
  key?: string;
  options?: string[];
  /** Select and append more than one column at a time. */
  isMultiSelect?: boolean;
  /** Option labels or values omitted from the selector. */
  excludeOptions?: string[];
  /** Allow typing to filter the loaded options. */
  searchable?: boolean;
  placeholder?: string;
  apiUrl?: string;
  method?: string;
  responseDataKey?: string;
  /**
   * Dot-path into the response used to reach the raw options array when it's
   * nested (e.g. '0.payload.attributesList' for a single-row entity_store lookup).
   * Takes precedence over responseDataKey/key when set.
   */
  optionsPath?: string;
  /** Object field rendered as the visible option text (default: same as the value). */
  optionLabelKey?: string;
  /** Object field used as the option's underlying value/payload (default: same as the label). */
  optionValueKey?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  isQueryParam?: boolean;
}

/** Resolved toolbar-select option — `label` is shown, `value` is what gets used everywhere else. */
export interface TextareaToolbarOption {
  label: string;
  value: string;
}

/** Single-field prompt shown before an `api` toolbar action fires. */
export interface TextareaToolbarPrompt {
  /** Caption above the field. */
  title?: string;
  /** Seed text for the input. */
  value?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
  /** Block the confirm button until something is typed (default true). */
  required?: boolean;
  maxLength?: number;
  /** Payload field the entered text is sent as (default 'name'). */
  key?: string;
  dialogWidth?: string;
}

export interface TextareaToolbarButton {
  label: string;
  /** 'append' pushes the toolbar selection into the textarea; 'api' fires the configured request. */
  action?: 'append' | 'api';
  /** Visual treatment for the toolbar action. */
  style?: 'primary' | 'secondary' | 'danger';
  apiUrl?: string;
  method?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  /** Ask for a name first; the request only fires once the prompt is confirmed. */
  prompt?: TextareaToolbarPrompt;
  /** Payload field carrying the textarea contents (default: this widget's `key`). */
  valueKey?: string;
  /**
   * Global-filter keys (e.g. other dropdowns on the page) whose current
   * selections are collected into a nested object and sent as `filterParam`.
   */
  filterKeys?: string | string[];
  /** Rename UI filter keys only in the request payload. */
  filterKeyMap?: Record<string, string>;
  /** requestData field the collected filter selections are nested under (default 'filter'). */
  filterParam?: string;
  /** Global-filter selections that must be present before this action can run. */
  requiredFilterKeys?: string | string[];
  /** Message shown when one of `requiredFilterKeys` has no selection. */
  requiredFilterError?: string;
  /** After a successful request, open and refresh the configured saved-selection picker. */
  openSavedSelectionOnSuccess?: boolean;
}

export interface TextareaToolbarConfig {
  label?: string;
  select?: TextareaToolbarSelect;
  buttons?: TextareaToolbarButton[];
}

/** One entry of the saved-selection list (left panel). */
export interface SavedSelectionItem {
  name: string;
  columns: string[];
  raw?: unknown;
}

export interface SavedSelectionButton {
  label: string;
  /**
   * 'load' applies the highlighted selection; 'cancel' restores the textarea
   * (or, while editing, discards the in-progress column removals); 'edit' opens
   * the highlighted entry's column list for removal; 'save' persists the edited
   * list through this button's own request.
   */
  action?: 'load' | 'cancel' | 'edit' | 'save';
  style?: 'primary' | 'secondary';
  /** Rendered greyed out and inert — for slots the screen shows but does not wire yet. */
  disabled?: boolean;

  /** 'save' only: the request that writes the edited column list back. Same
   *  request contract as the saved list / toolbar buttons. */
  apiUrl?: string;
  method?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  /** Optional route opened after the highlighted selection is loaded. */
  navigateTo?: string;
  /**
   * Swap the card body to the configured `resultView` after loading, instead
   * of leaving the page. Use this rather than `navigateTo` when the result
   * belongs on the same screen.
   */
  showResultView?: boolean;
}

/**
 * A request fired by the same click that opens the result view, ahead of the
 * result widget's own call. Its response is merged into the widget's
 * `customPayload` — i.e. into the `requestData` of the widget's request — so a
 * service that resolves the current selections can feed the one that returns
 * the rows.
 */
export interface TextareaResultPreRequestConfig {
  /** Same request contract as the toolbar select / saved list. */
  apiUrl?: string;
  method?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  /**
   * Global-filter keys whose current selections are sent as requestData.
   * Omitted: the loaded selection's own filters and columns are sent instead.
   */
  filterKeys?: string | string[];
  /** Rename UI filter keys only in the pre-request payload. */
  filterKeyMap?: Record<string, string>;
  /** Include the current column-selection values in requestData. */
  includeSelectedColumns?: boolean;
  /** Map current selections into the configured requestData.filters array. */
  requestParamsAsFilterRules?: {
    filterKeys?: string[];
    operator?: string;
    multipleOperator?: string;
    logicalOperator?: string;
    firstLogicalOperator?: string;
    additionalLogicalOperator?: string;
    attributeValueAsArray?: boolean;
    insertBefore?: string[];
  };
  /** Dot-path to the object to merge when the response nests it (default: the response itself). */
  responsePath?: string;
  /** Use this request's rows directly in the result table, avoiding its own API call. */
  useResponseAsInitialRows?: boolean;
  /** Dot-path to the row array when `useResponseAsInitialRows` is enabled. */
  rowsPath?: string;
  /**
   * false: open the result widget straight away and let this request run
   * alongside it — its response is then not merged into the widget's payload.
   */
  awaitResponse?: boolean;
  /** Note shown in the card body while the request is in flight. */
  loadingMessage?: string;
  /** Re-run the visible result request when one of filterKeys changes (default true). */
  reloadOnFilterChange?: boolean;
  /** Convert response rows into the record-list structure required by the result service. */
  recordListPayload?: {
    rowsPath?: string;
    /** Columnar-response field that supplies one output record per value. */
    recordKey?: string;
    /** Fields required on each record built from a columnar response. */
    recordFields?: string[];
    listKey?: string;
    mapKey?: string;
    mapListKey?: string;
    scalarFilterKeys?: string[];
    requireNonEmpty?: boolean;
  };
}

/**
 * Third body view for the card: a widget config rendered where the textarea
 * and the saved picker normally sit. Everything except `type` and `preRequest`
 * is the widget's own JSON contract and is handed over untouched.
 */
export interface TextareaResultViewConfig {
  /** Widget type rendered in the card body (currently `json-driven-smart-table`). */
  type?: string;
  /** Optional request fired before the widget mounts; never forwarded to it. */
  preRequest?: TextareaResultPreRequestConfig;
  [key: string]: any;
}

/**
 * Swaps the textarea inside this same card for a two-panel picker
 * (saved list on the left, its columns on the right). No extra dialog:
 * `openOnKey` flips the body, `closeOnKey`/Cancel flips it back.
 */
export interface SavedSelectionConfig {
  /** Global-filter key (usually a button) that swaps the body into the picker. */
  openOnKey?: string;
  /** Global-filter key that swaps it back — e.g. the Column Selection button. */
  closeOnKey?: string;
  /** Toggle-group key whose active option should follow an Edit into column selection. */
  viewKey?: string;
  /** Open Edit in the main add/remove column workspace instead of the saved panel. */
  editInColumnSelection?: boolean;
  /** Value written to `viewKey` while the configured result widget is visible. */
  resultViewState?: string;

  listTitle?: string;
  panelTitle?: string;
  buttons?: SavedSelectionButton[];
  /**
   * Update request used by the toolbar Save button when Edit opens in the
   * column-selection workspace. Kept separate from `buttons` because it is an
   * action configuration, not a button rendered in the saved-selection panel.
   */
  save?: SavedSelectionButton;

  /** Static entries; used as-is when no `apiUrl` is configured. */
  items?: SavedSelectionItem[];

  /** Remote entries — same request/response contract as the toolbar select. */
  apiUrl?: string;
  method?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  responseDataKey?: string;
  /** Field names to read each entry's label and columns from. */
  nameKey?: string;
  columnsKey?: string;
  /**
   * Field on each entry holding a { filterKey: value } map to restore into the
   * page's global filters (e.g. other dropdowns) when Load is clicked. Default 'filter'.
   */
  filterKey?: string;
  /** Complete filter snapshot keys restored/cleared when saved selections change. */
  filterKeys?: string | string[];
  /** Stored/payload key for each UI filter key (e.g. customer_no -> bill_to_code). */
  filterKeyMap?: Record<string, string>;
  /** Retain a current page selection when the saved record omits that filter key. */
  preserveCurrentFiltersOnLoad?: boolean;
  /** Clear filters and columns when entering the ordinary Column Selection view. */
  resetOnColumnSelection?: boolean;

  /**
   * Fields copied verbatim off the stored record into a 'save' request, so the
   * backend updates that row instead of inserting a new one. Whatever is named
   * here must also be requested by the list call (its `attributesList`), or it
   * will not be present on the record to copy. Default `['uuid']`.
   */
  identityKeys?: string | string[];

  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  /** Shown in the columns panel when a 'save' request fails. */
  saveErrorMessage?: string;
  /** Shown in the columns panel while a 'save' request is in flight. */
  savingMessage?: string;
}

/**
 * JSON contract for the `textarea` / `dialog-box` widget. Everything the widget
 * does is driven from here — nothing about a particular screen is baked in.
 */
export interface TextareaBoxConfig {
  /** Wrap the widget in a rounded card frame (Regional Price Files style). */
  card?: boolean;
  /** Optional label + select + buttons row rendered above the textarea. */
  toolbar?: TextareaToolbarConfig;
  /** Field caption rendered above the box. */
  title?: string;
  label?: string;

  /** Global-filter key this widget publishes its value under. */
  key?: string;
  isQueryParam?: boolean;

  /** Seed content. */
  value?: string;
  text?: string;

  placeholder?: string;
  rows?: number;
  height?: string;
  /** Height of the textarea itself; `height` then sizes only the grid cell
   *  (useful in card mode where the toolbar shares the cell). */
  textareaHeight?: string;

  readonly?: boolean;
  disabled?: boolean;
  /** Render values as individually removable rows instead of textarea text. */
  removableSelectionList?: boolean;

  /** Validation. */
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  errorMessages?: Record<string, string>;
  showCharCount?: boolean;

  /**
   * Keys of other widgets (dropdowns, buttons) whose selections feed this box.
   * With `appendOnKeys` the box only pulls from them when one of those keys
   * fires, which is how a "Add" button commits the current dropdown choice.
   */
  sourceKeys?: string | string[];
  appendOnKeys?: string | string[];
  separator?: string;
  /**
   * Separator used when this widget's value is published/sent to the backend
   * (Save, Generate, etc.) — defaults to `separator`. Lets the box display
   * items one-per-line (`separator: '\n'`) while still round-tripping a
   * comma-joined string the API expects (`payloadSeparator: ','`).
   */
  payloadSeparator?: string;
  /** Publish/send each textarea item as a separate array entry. */
  payloadAsArray?: boolean;
  allowDuplicates?: boolean;
  /** Wipe back to the seed value when the global Reset clears every filter. */
  resetOnClearAll?: boolean;

  /**
   * Global-filter key (usually a Back button) that steps the card body one view
   * back towards the textarea: result view → saved picker → textarea.
   */
  backOnKey?: string;
  /** External destination used when Back is clicked from the base column-selection view. */
  columnSelectionBackUrl?: string;
  /** Environment property containing the Column Selection Back destination. */
  columnSelectionBackEnvironmentKey?: string;

  /** 'inline' (default) renders the box in the grid; 'dialog' renders it in a modal. */
  displayMode?: 'inline' | 'dialog';
  /** Global-filter key (usually a button) that opens the modal. */
  openOnKey?: string;
  dialogTitle?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  dialogWidth?: string;

  /** Optional saved-selection picker rendered in place of the textarea. */
  savedSelection?: SavedSelectionConfig;

  /**
   * Optional result widget rendered in place of the textarea once a saved
   * selection is loaded (see `SavedSelectionButton.showResultView`). Keeps the
   * result on this screen instead of routing to a page of its own.
   */
  resultView?: TextareaResultViewConfig;
}

@Component({
  selector: 'app-textarea-box',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    NgSelectModule,
    JsonDrivenSmartTableComponent,
  ],
  templateUrl: './textarea-box.component.html',
  styleUrl: './textarea-box.component.scss',
})
export class TextareaBoxComponent implements OnInit, OnDestroy {
  @Input() data: TextareaBoxConfig = {};

  @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<unknown>;
  @ViewChild('toolbarSearchInput') toolbarSearchInput?: ElementRef<HTMLInputElement>;

  control = new FormControl<string>('');

  /** Toolbar select state. */
  toolbarOptions: TextareaToolbarOption[] = [];
  filteredToolbarOptions: TextareaToolbarOption[] = [];
  toolbarValue: string | string[] = '';
  toolbarSearchText = '';
  toolbarActionError = '';

  /** Saved-selection state — while `savedMode` is on the card body shows the picker. */
  savedMode = false;
  savedItems: SavedSelectionItem[] = [];
  activeSavedItem: SavedSelectionItem | null = null;
  savedLoading = false;
  savedError = '';

  /** Edit mode: each entry in the columns panel gets a remove control. */
  savedEditMode = false;
  savedSaving = false;
  savedEditError = '';
  /**
   * Working copy of the highlighted entry's columns while editing. The stored
   * entry is left untouched until a save succeeds, so Cancel restores the
   * original list by simply dropping this.
   */
  private savedEditColumns: string[] | null = null;
  /** Dynamic filter keys restored by the last saved record, so the next one can clear them. */
  private appliedSavedFilterKeys = new Set<string>();
  /** Newly inserted name to highlight after the saved list has refreshed. */
  private preferredSavedItemName = '';

  /** Result view state — while `resultMode` is on the card body shows `resultView`. */
  resultMode = false;
  /** `resultView` with the pre-request response folded in; what the widget is handed. */
  resolvedResultView: TextareaResultViewConfig | null = null;
  resultLoading = false;
  resultError = '';

  private readonly destroy$ = new Subject<void>();
  private dialogRef?: MatDialogRef<unknown>;
  private seedValue = '';

  constructor(
    private globalFilterService: GlobalFilterService,
    private configService: ConfigService,
    private dialog: MatDialog,
    private confirmationDialogService: ConfirmationDialogService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.seedValue = String(this.data?.value ?? this.data?.text ?? '');
    this.control.setValue(this.seedValue, { emitEvent: false });
    this.control.setValidators(this.buildValidators());
    this.control.updateValueAndValidity({ emitEvent: false });

    if (this.data?.disabled) {
      this.control.disable({ emitEvent: false });
    }

    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.publish(value ?? ''));

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => this.onFiltersChanged(filters));

    if (this.seedValue) {
      this.publish(this.seedValue);
    }

    this.loadToolbarOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dialogRef?.close();
  }

  // ---------------------------------------------------------------- template

  get labelText(): string {
    return String(this.data?.title ?? this.data?.label ?? '');
  }

  get placeholder(): string {
    return String(this.data?.placeholder ?? '');
  }

  get rows(): number {
    const rows = Number(this.data?.rows);
    return Number.isFinite(rows) && rows > 0 ? rows : 6;
  }

  get fieldHeight(): string | null {
    const height = this.data?.textareaHeight ?? this.data?.height;
    return height ? String(height) : null;
  }

  /**
   * Card mode stretches the frame to its grid cell and lets the body flex into
   * whatever is left over, so pinning a height here would only re-open the dead
   * space under the box. Inline (non-card) usage keeps the configured height.
   */
  get fieldFixedHeight(): string | null {
    return this.isCard ? null : this.fieldHeight;
  }

  get isReadonly(): boolean {
    return this.data?.readonly === true;
  }

  get isRequired(): boolean {
    return this.data?.required === true;
  }

  get maxLength(): number | null {
    const max = Number(this.data?.maxLength);
    return Number.isFinite(max) && max > 0 ? max : null;
  }

  get showCharCount(): boolean {
    return this.data?.showCharCount === true;
  }

  get charCount(): number {
    return (this.control.value || '').length;
  }

  get isDialogMode(): boolean {
    return this.data?.displayMode === 'dialog';
  }

  get dialogTitle(): string {
    return String(this.data?.dialogTitle ?? this.labelText ?? '');
  }

  get confirmLabel(): string {
    return String(this.data?.confirmLabel ?? 'Save');
  }

  get cancelLabel(): string {
    return String(this.data?.cancelLabel ?? 'Cancel');
  }

  get isCard(): boolean {
    return this.data?.card === true;
  }

  get toolbar(): TextareaToolbarConfig | null {
    return this.data?.toolbar ?? null;
  }

  get toolbarButtons(): TextareaToolbarButton[] {
    return this.toolbar?.buttons ?? [];
  }

  onToolbarSearchChange(value: unknown): void {
    this.toolbarSearchText = String(value ?? '');
    this.syncFilteredToolbarOptions();
  }

  onToolbarDropdownOpen(): void {
    // The header template is created as the panel opens, so defer focus until
    // ng-select has attached the search input (including when appended to body).
    setTimeout(() => this.toolbarSearchInput?.nativeElement.focus());
  }

  get areAllToolbarOptionsSelected(): boolean {
    const visibleValues = this.filteredToolbarOptions.map((option) => option.value);
    const selected = new Set(this.toList(this.toolbarValue));
    return visibleValues.length > 0 && visibleValues.every((value) => selected.has(value));
  }

  isToolbarOptionSelected(option: TextareaToolbarOption): boolean {
    return this.toList(this.toolbarValue).includes(option.value);
  }

  toolbarSelectionText(items: Array<TextareaToolbarOption | string> | null | undefined): string {
    const labels = this.toolbarSelectionLabels(items);
    const visibleLabels = labels.slice(0, 2).join(', ');
    const hiddenCount = labels.length - 2;
    return hiddenCount > 0 ? `${visibleLabels} +${hiddenCount}` : visibleLabels;
  }

  toolbarSelectionTitle(items: Array<TextareaToolbarOption | string> | null | undefined): string {
    return this.toolbarSelectionLabels(items).join(', ');
  }

  private toolbarSelectionLabels(
    items: Array<TextareaToolbarOption | string> | null | undefined,
  ): string[] {
    return (items ?? [])
      .map((item) => typeof item === 'string' ? this.columnDisplayLabel(item) : item?.label)
      .map((label) => String(label ?? '').trim())
      .filter(Boolean);
  }

  toggleAllToolbarOptions(event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    const visibleValues = this.filteredToolbarOptions.map((option) => option.value);
    const selected = new Set(this.toList(this.toolbarValue));
    visibleValues.forEach((value) => checked ? selected.add(value) : selected.delete(value));
    this.onToolbarValueChange([...selected]);
  }

  get showCancelColumnEdit(): boolean {
    return this.savedEditMode && this.savedSelection?.editInColumnSelection === true;
  }

  get columnSelectionValues(): string[] {
    const separator = this.data?.separator ?? ', ';
    return this.toPayloadList(this.splitValue(this.control.value ?? '', separator));
  }

  /** Resolve a backend key through the label/value options used by Columns/Fields. */
  columnDisplayLabel(column: string): string {
    const normalized = String(column ?? '').trim();
    return this.toolbarOptions.find((option) => option.value === normalized)?.label ?? normalized;
  }

  onToolbarButton(button: TextareaToolbarButton): void {
    const action = button?.action ?? 'append';
    this.toolbarActionError = '';

    if (action === 'append') {
      const selectedColumns = this.toList(this.toolbarValue);
      if (selectedColumns.length) {
        this.applyValues(this.toUniqueColumnValues(selectedColumns), true);
      }
      return;
    }

    // A required column-selection field must contain an actual column before
    // either the Save As prompt or an edit-save request can start. Other page
    // filters are deliberately irrelevant to this check.
    if (this.isRequired && !this.columnSelectionValues.length) {
      this.control.setErrors({ ...(this.control.errors || {}), required: true });
      this.control.markAllAsTouched();
      return;
    }

    if (!this.hasRequiredFilters(button)) {
      return;
    }

    // In the shared column-selection workspace, Save updates the highlighted
    // saved record instead of prompting for and inserting a new record.
    if (this.savedEditMode && this.savedSelection?.editInColumnSelection === true) {
      const updateButton =
        this.savedSelection.save ??
        this.savedButtons.find((candidate) => candidate.action === 'save');
      if (updateButton) {
        this.saveSavedEdit(updateButton);
      } else {
        this.savedEditError = 'Unable to save: no update request is configured.';
      }
      return;
    }

    const apiUrl = String(button?.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    if (button.prompt) {
      this.promptThenSubmit(button, apiUrl);
      return;
    }

    this.submitToolbarAction(button, apiUrl);
  }

  /** Collect a name first — the request only fires once the prompt is confirmed. */
  private promptThenSubmit(button: TextareaToolbarButton, apiUrl: string): void {
    const prompt = button.prompt || {};
    const inputKey = 'value';

    this.confirmationDialogService
      .open(
        {
          title: prompt.title || 'Save as',
          hideCloseButton: true,
          confirmLabel: prompt.okLabel || 'Ok',
          cancelLabel: prompt.cancelLabel || 'Cancel',
          inputs: [
            {
              key: inputKey,
              type: 'text',
              required: prompt.required !== false,
              defaultValue: prompt.value,
              placeholder: prompt.placeholder,
              maxLength: prompt.maxLength,
            },
          ],
        },
        { width: prompt.dialogWidth || '480px' },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const confirmed = typeof result === 'object' ? result.confirmed : result;
        if (!confirmed) {
          return;
        }
        const values = typeof result === 'object' ? result.values || {} : {};
        const name = String(values[inputKey] ?? '').trim();
        this.submitToolbarAction(button, apiUrl, name);
      });
  }

  private submitToolbarAction(
    button: TextareaToolbarButton,
    apiUrl: string,
    name?: string,
  ): void {
    // Recheck after the Save As dialog: a page dropdown can be cleared while
    // that dialog is open, and the insert must still remain blocked.
    if (!this.hasRequiredFilters(button)) {
      return;
    }

    const value = this.control.value ?? '';
    // Publish the current textarea value first, then fire the request so the
    // payload service sees it in the filters.
    this.publish(value);

    // getConfig merges these into customPayload.requestData, which is how the
    // textarea contents and the entered name reach the backend.
    const requestParams: Record<string, unknown> = {};
    const valueKey = String(button.valueKey || this.data?.key || '').trim();
    if (valueKey) {
      requestParams[valueKey] = this.toPayloadValue(value);
    }
    if (name !== undefined) {
      requestParams[String(button.prompt?.key || 'name')] = name;
    }

    const filterKeys = this.toList(button.filterKeys);
    if (filterKeys.length) {
      requestParams[String(button.filterParam || 'filter')] = this.buildFilterSelections(
        filterKeys,
        button.filterKeyMap,
      );
    }

    this.configService.getConfig(apiUrl, button, { requestParams }, button.method).subscribe({
      next: () => {
        if (button.openSavedSelectionOnSuccess === true) {
          this.openSavedSelectionAfterSave(name);
        }
      },
      error: (err: unknown) =>
        console.error(`textarea-box: action failed for ${button.label}`, err),
    });
  }

  private hasCurrentFilterValue(key: string): boolean {
    const current = this.globalFilterService.getCurrentFilters();
    const value = current?.queryParams?.[key] ?? current?.requestParams?.[key];
    return Array.isArray(value)
      ? value.flat(Infinity).some((item) => item !== null && item !== undefined && String(item).trim() !== '')
      : value !== null && value !== undefined && String(value).trim() !== '';
  }

  private hasRequiredFilters(button: TextareaToolbarButton): boolean {
    const missingRequiredFilters = this.toList(button.requiredFilterKeys)
      .filter((key) => !this.hasCurrentFilterValue(key));
    if (!missingRequiredFilters.length) {
      return true;
    }

    this.toolbarActionError = String(
      button.requiredFilterError ?? 'Please select the required filters before saving.',
    );
    return false;
  }

  private openSavedSelectionAfterSave(name?: string): void {
    this.preferredSavedItemName = String(name ?? '').trim();
    this.setSavedSelectionView(this.savedSelection?.openOnKey);

    // updateFilterSelection emits synchronously in the application. Keep this
    // fallback for hosts/tests whose filter service does not echo the change.
    if (!this.savedMode) {
      this.openSavedSelection();
    }
  }

  onToolbarValueChange(value: unknown): void {
    this.toolbarValue = this.toolbar?.select?.isMultiSelect === true
      ? this.toList(value)
      : String(value ?? '').trim();
    const key = String(this.toolbar?.select?.key || '').trim();
    if (key) {
      this.globalFilterService.updateFilterSelection(
        key,
        this.toList(this.toolbarValue),
        this.toolbar?.select?.isQueryParam,
        undefined,
        false,
      );
    }
  }

  // ----------------------------------------------------------- saved picker

  get savedSelection(): SavedSelectionConfig | null {
    return this.data?.savedSelection ?? null;
  }

  get savedListTitle(): string {
    return String(this.savedSelection?.listTitle ?? 'Saved Filters');
  }

  get savedPanelTitle(): string {
    return String(this.savedSelection?.panelTitle ?? 'Selected Columns');
  }

  get savedButtons(): SavedSelectionButton[] {
    return (
      this.savedSelection?.buttons ?? [
        { label: 'Load', action: 'load', style: 'primary' },
        { label: 'Cancel', action: 'cancel', style: 'secondary' },
      ]
    );
  }

  get savedEmptyMessage(): string {
    return String(this.savedSelection?.emptyMessage ?? 'No saved selections found.');
  }

  get savedLoadingMessage(): string {
    return String(this.savedSelection?.loadingMessage ?? 'Loading saved selections...');
  }

  /**
   * Deduped here rather than at each use: a stored selection can carry the same
   * column twice (saved before this rule existed, or written by another screen),
   * and a repeated column makes the backend reject the record outright. Covers
   * the panel the user reads, the textarea Load fills, and the payload alike.
   */
  get activeSavedColumns(): string[] {
    // While editing, every reader (the panel, Load, and the save payload) must
    // see the working copy rather than the stored list.
    const source = this.savedEditMode && this.savedEditColumns
      ? this.savedEditColumns
      : this.activeSavedItem?.columns ?? [];
    return this.toUniqueValues(source);
  }

  get savingMessage(): string {
    return String(this.savedSelection?.savingMessage ?? 'Saving...');
  }

  isActiveSavedItem(item: SavedSelectionItem): boolean {
    return this.activeSavedItem === item;
  }

  selectSavedItem(item: SavedSelectionItem): void {
    if (item === this.activeSavedItem) {
      this.setColumnSelectionValues(this.activeSavedColumns);
      this.applySavedFilters();
      return;
    }
    // Edit mode belongs to one entry — moving the highlight drops the
    // in-progress removals rather than silently carrying them across records.
    this.exitSavedEdit();
    this.activeSavedItem = item;
    this.setColumnSelectionValues(this.activeSavedColumns);
    this.applySavedFilters();
  }

  isSavedButtonDisabled(button: SavedSelectionButton): boolean {
    if (button?.disabled) {
      return true;
    }

    const action = button?.action ?? 'load';

    if (action === 'cancel') {
      return false;
    }
    if (this.savedSaving) {
      return true;
    }
    // Edit opens the list; it is meaningless once open, or with nothing to edit.
    if (action === 'edit') {
      return this.savedEditMode || !this.activeSavedColumns.length;
    }
    // Save only commits an edit that is actually in progress.
    if (action === 'save') {
      return !this.savedEditMode || !this.activeSavedColumns.length;
    }
    return !this.activeSavedColumns.length;
  }

  /** Remove one column from the working copy. Stored entry stays untouched. */
  removeSavedColumn(column: string): void {
    if (!this.savedEditMode || !this.savedEditColumns) {
      return;
    }
    this.savedEditColumns = this.savedEditColumns.filter((value) => value !== column);
    this.savedEditError = '';
  }

  removeColumnSelection(column: string): void {
    this.setColumnSelectionValues(
      this.columnSelectionValues.filter((value) => value !== column),
    );

    // The removable list and the toolbar dropdown represent the same column
    // selection. Keep the dropdown model in sync so removing a row also
    // clears its checkbox (and updates the published toolbar filter value).
    const toolbarValues = this.toList(this.toolbarValue);
    if (toolbarValues.includes(column)) {
      this.onToolbarValueChange(toolbarValues.filter((value) => value !== column));
    }
  }

  cancelColumnSelectionEdit(): void {
    if (!this.showCancelColumnEdit) return;

    this.discardColumnSelectionEdit();
    this.resultMode = false;
    this.savedMode = true;

    const viewKey = String(this.savedSelection?.viewKey || '').trim();
    const savedSelectionKey = String(this.savedSelection?.openOnKey || '').trim();
    if (viewKey && savedSelectionKey) {
      this.globalFilterService.updateFilterSelection(viewKey, [savedSelectionKey]);
    }
  }

  private discardColumnSelectionEdit(): void {
    const originalColumns = [...(this.activeSavedItem?.columns ?? [])];
    this.exitSavedEdit();
    this.setColumnSelectionValues(originalColumns);
    this.savedEditError = '';
  }

  onSavedButton(button: SavedSelectionButton): void {
    if (this.isSavedButtonDisabled(button)) {
      return;
    }

    const action = button?.action ?? 'load';

    if (action === 'edit') {
      this.savedEditColumns = [...this.activeSavedColumns];
      this.savedEditMode = true;
      this.savedEditError = '';

      if (this.savedSelection?.editInColumnSelection === true) {
        this.setColumnSelectionValues(this.savedEditColumns);
        this.toolbarValue = this.toolbar?.select?.isMultiSelect === true
          ? [...this.savedEditColumns]
          : this.savedEditColumns[0] ?? '';
        this.toolbarSearchText = '';
        this.syncFilteredToolbarOptions();
        this.savedMode = false;
        this.resultMode = false;

        const viewKey = String(this.savedSelection.viewKey || '').trim();
        const columnSelectionKey = String(this.savedSelection.closeOnKey || '').trim();
        if (viewKey && columnSelectionKey) {
          this.globalFilterService.updateFilterSelection(viewKey, [columnSelectionKey]);
        }
      }
      return;
    }

    if (action === 'save') {
      this.saveSavedEdit(button);
      return;
    }

    if (action === 'cancel') {
      // While editing, Cancel means "discard my removals" and keeps the picker
      // open; outside edit mode it still closes the picker as before.
      if (this.savedEditMode) {
        this.exitSavedEdit();
        return;
      }
      this.openFreshColumnSelection();
      return;
    }

    // Load writes the saved columns into the textarea; the resulting
    // valueChanges republishes them so the table picks them up.
    const separator = this.data?.separator ?? ', ';
    this.control.setValue(this.activeSavedColumns.join(separator));
    this.control.markAsDirty();
    this.applySavedFilters();
    this.closeSavedSelection();

    if (button?.showResultView && this.resultView) {
      // Reads the selections the two lines above just restored.
      this.openResultView();
      return;
    }

    const navigateTo = String(button?.navigateTo || '').trim();
    if (navigateTo) {
      const requestParams = this.buildSavedRequestParams();
      const columnsKey = String(this.savedSelection?.columnsKey || 'selected_columns').trim();
      void this.router.navigateByUrl(navigateTo, {
        state: {
          requestParams,
          // Keep the loaded selection separate from the filter map. The
          // destination reapplies this snapshot after merging navigation
          // filters, so filter field names cannot leak into the column list.
          loadedSelection: {
            key: columnsKey,
            values: this.toPayloadList(this.activeSavedColumns),
          },
        },
      });
    }
  }

  /**
   * Persist the edited column list. The stored entry is only rewritten once the
   * request succeeds, so a failure leaves both the panel and the record on the
   * pre-save state and the user can retry without redoing the removals.
   */
  private saveSavedEdit(button: SavedSelectionButton): void {
    const columns = this.savedSelection?.editInColumnSelection === true
      ? this.columnSelectionValues
      : this.activeSavedColumns;
    const apiUrl = String(button?.apiUrl || '').trim();

    // No endpoint wired: keep the edit locally so the panel still reflects it.
    if (!apiUrl) {
      this.finishSavedEdit(columns);
      return;
    }

    this.savedSaving = true;
    this.savedEditError = '';

    this.configService
      .getConfig(apiUrl, button, { requestParams: this.buildSavedEditParams(columns) }, button.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savedSaving = false;
          this.finishSavedEdit(columns);
        },
        error: (err: unknown) => {
          this.savedSaving = false;
          this.savedEditError = String(
            this.savedSelection?.saveErrorMessage ?? 'Unable to save the updated columns.',
          );
          console.error('textarea-box: saved-selection save failed', err);
        },
      });
  }

  /** Commit a successful edit, return to Saved Selection, and keep that row highlighted. */
  private finishSavedEdit(columns: string[]): void {
    const editedName = this.activeSavedItem?.name;
    this.commitSavedEdit(columns);
    this.openSavedSelectionAfterSave(editedName);
  }

  /**
   * The edited column list plus the fields that identify which stored record it
   * belongs to — without those the backend would insert a new row rather than
   * update this one.
   */
  private buildSavedEditParams(columns: string[]): Record<string, unknown> {
    const config = this.savedSelection;
    const requestParams: Record<string, unknown> = {};
    const raw = (this.activeSavedItem?.raw ?? {}) as Record<string, unknown>;

    const columnsKey = String(config?.columnsKey || 'selected_columns').trim();
    if (columnsKey) {
      requestParams[columnsKey] = this.toPayloadList(columns);
    }

    const nameKey = String(config?.nameKey || 'name').trim();
    if (nameKey && this.activeSavedItem?.name) {
      requestParams[nameKey] = this.activeSavedItem.name;
    }

    // Refresh the configured filters from the current UI when an existing
    // selection is edited. Start with the stored map so fields outside this
    // screen's contract survive, then replace configured keys (including
    // removing a configured value that the user cleared).
    const filterKey = String(config?.filterKey || 'filter').trim();
    const configuredFilterKeys = this.toList(config?.filterKeys);
    if (filterKey && configuredFilterKeys.length) {
      const filterMap = { ...(this.getActiveSavedFilterMap() ?? {}) };
      configuredFilterKeys.forEach((key) => {
        delete filterMap[key];
        delete filterMap[config?.filterKeyMap?.[key] || key];
      });
      Object.assign(
        filterMap,
        this.buildFilterSelections(configuredFilterKeys, config?.filterKeyMap),
      );
      requestParams[filterKey] = filterMap;
    } else if (filterKey && raw[filterKey] !== undefined) {
      requestParams[filterKey] = raw[filterKey];
    }

    const identityKeys = this.toList(config?.identityKeys ?? ['uuid']);
    identityKeys.forEach((key) => {
      if (raw[key] !== undefined) {
        requestParams[key] = raw[key];
      }
    });

    return requestParams;
  }

  /** Write the edited list onto the highlighted entry and leave edit mode. */
  private commitSavedEdit(columns: string[]): void {
    if (this.activeSavedItem) {
      this.activeSavedItem.columns = [...columns];
    }
    this.exitSavedEdit();
  }

  /** Drop the working copy — the stored entry was never modified. */
  private exitSavedEdit(): void {
    this.savedEditMode = false;
    this.savedEditColumns = null;
    this.savedEditError = '';
  }

  private buildSavedRequestParams(): Record<string, unknown> {
    const requestParams: Record<string, unknown> = {};
    const filterMap = this.getActiveSavedFilterMap();

    if (filterMap) {
      Object.assign(requestParams, filterMap);
    }

    const columnsKey = String(this.savedSelection?.columnsKey || 'selected_columns').trim();
    if (columnsKey) {
      requestParams[columnsKey] = this.toPayloadList(this.activeSavedColumns);
    }
    return requestParams;
  }

  /** Restores the saved entry's filter selections into the page's other dropdowns. */
  private applySavedFilters(): void {
    const filterMap = this.getActiveSavedFilterMap() ?? {};
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const currentBag = {
      ...(currentFilters?.queryParams || {}),
      ...(currentFilters?.requestParams || {}),
    };
    const preserveCurrent = this.savedSelection?.preserveCurrentFiltersOnLoad === true;
    const filterKeyMap = this.savedSelection?.filterKeyMap ?? {};
    const uiKeyByStoredKey = Object.entries(filterKeyMap).reduce<Record<string, string>>(
      (result, [uiKey, storedKey]) => {
        result[storedKey] = uiKey;
        return result;
      },
      {},
    );
    const savedUiKeys = Object.keys(filterMap).map((storedKey) => uiKeyByStoredKey[storedKey] || storedKey);
    const filterKeys = [...new Set([
      ...this.toList(this.savedSelection?.filterKeys),
      ...this.appliedSavedFilterKeys,
      ...savedUiKeys,
    ])];

    const selections = filterKeys.reduce<Record<string, any[]>>((result, key) => {
      const storedKey = filterKeyMap[key] || key;
      const hasMappedValue = Object.prototype.hasOwnProperty.call(filterMap, storedKey);
      const hasSavedValue = hasMappedValue || Object.prototype.hasOwnProperty.call(filterMap, key);
      const value = hasSavedValue
        ? filterMap[hasMappedValue ? storedKey : key]
        : preserveCurrent
          ? currentBag[key]
          : undefined;
      result[key] = this.normalizeSavedFilterValues(value);
      return result;
    }, {});

    this.appliedSavedFilterKeys = new Set([
      ...this.toList(this.savedSelection?.filterKeys),
      ...savedUiKeys,
    ]);
    this.globalFilterService.updateFilterSelections(selections);
  }

  /** Treat null, blank, and nested-empty saved values as an explicitly cleared dropdown. */
  private normalizeSavedFilterValues(value: unknown): any[] {
    const values = (Array.isArray(value) ? value.flat(Infinity) : [value]);
    return values.filter((item) =>
      item !== null
      && item !== undefined
      && (typeof item !== 'string' || item.trim().length > 0)
    );
  }

  /** Accept object or JSON-string filter payloads returned by saved-filter APIs. */
  private getActiveSavedFilterMap(): Record<string, unknown> | null {
    const filterKey = String(this.savedSelection?.filterKey || 'filter').trim();
    const raw = this.activeSavedItem?.raw as Record<string, any> | undefined;
    let candidate: any = raw?.[filterKey] ?? raw?.['requestData']?.[filterKey] ?? raw?.['payload']?.[filterKey];

    if (typeof candidate === 'string') {
      try {
        candidate = JSON.parse(candidate);
      } catch {
        return null;
      }
    }

    return candidate && typeof candidate === 'object' && !Array.isArray(candidate)
      ? candidate as Record<string, unknown>
      : null;
  }

  private openSavedSelection(): void {
    if (!this.savedSelection) {
      return;
    }
    this.resultMode = false;
    this.resultLoading = false;
    this.savedMode = true;
    this.exitSavedEdit();
    this.loadSavedItems();
  }

  private closeSavedSelection(): void {
    this.savedMode = false;
    // Leaving the picker abandons any half-finished edit; reopening starts clean.
    this.exitSavedEdit();
  }

  /**
   * Walks the card body one view back towards the textarea:
   * result view → saved picker → textarea. A no-op on the textarea itself.
   */
  private stepBack(): void {
    if (this.resultMode) {
      this.resultMode = false;
      this.resultLoading = false;
      // Reuse the list already fetched so the highlighted entry survives.
      if (this.savedItems.length) {
        this.savedMode = true;
      } else {
        this.openSavedSelection();
      }
      this.setSavedSelectionView(this.savedSelection?.openOnKey);
      return;
    }

    if (this.savedMode) {
      this.openFreshColumnSelection();
      return;
    }

    const environmentKey = String(this.data?.columnSelectionBackEnvironmentKey || '').trim();
    const columnSelectionBackUrl = environmentKey
      ? this.configService.resolveEnvironmentValue(environmentKey)
      : String(this.data?.columnSelectionBackUrl || '').trim();
    if (columnSelectionBackUrl) {
      this.navigateToExternalUrl(columnSelectionBackUrl);
    }
  }

  private navigateToExternalUrl(url: string): void {
    window.location.assign(url);
  }

  private openFreshColumnSelection(): void {
    this.closeSavedSelection();
    this.resultMode = false;
    this.setSavedSelectionView(this.savedSelection?.closeOnKey);
    this.resetColumnSelectionWorkspace();
  }

  private resetColumnSelectionWorkspace(): void {
    if (this.savedSelection?.resetOnColumnSelection !== true) return;

    this.toList(this.savedSelection?.filterKeys).forEach((key) =>
      this.globalFilterService.updateFilterSelection(key, [])
    );
    const toolbarKey = String(this.toolbar?.select?.key || '').trim();
    this.toolbarValue = this.toolbar?.select?.isMultiSelect === true ? [] : '';
    this.toolbarSearchText = '';
    this.syncFilteredToolbarOptions();
    if (toolbarKey) {
      this.globalFilterService.updateFilterSelection(toolbarKey, [], false, undefined, false);
    }
    this.control.setValue(this.seedValue);
    this.control.markAsPristine();
    this.control.markAsUntouched();
  }

  // ------------------------------------------------------------ result view

  get resultView(): TextareaResultViewConfig | null {
    return this.data?.resultView ?? null;
  }

  get resultPreRequest(): TextareaResultPreRequestConfig | null {
    return this.resultView?.preRequest ?? null;
  }

  get resultLoadingMessage(): string {
    return String(this.resultPreRequest?.loadingMessage ?? 'Loading results...');
  }

  get isSmartTableResult(): boolean {
    const type = String(this.resultView?.type || '').trim();
    return type === 'json-driven-smart-table' || type === 'scenario-planner-table';
  }

  /**
   * Shows the result widget in this same card body. The selections
   * `applySavedFilters` restored, plus the columns the textarea republished,
   * are already in the global bag by the time the widget mounts and reads them.
   *
   * With a `preRequest` configured, the request goes out before the widget
   * mounts. Its response can feed another service or supply the table rows
   * directly when `useResponseAsInitialRows` is enabled.
   */
  private openResultView(): void {
    const preRequest = this.resultPreRequest;
    const apiUrl = String(preRequest?.apiUrl || '').trim();

    if (!preRequest || !apiUrl) {
      this.showResultView(null);
      return;
    }

    const requestConfig = this.buildPreRequestConfig(preRequest);
    const request$ = this.configService
      .getConfig(
        apiUrl,
        requestConfig,
        { requestParams: this.buildPreRequestParams(preRequest) },
        preRequest.method,
      )
      .pipe(takeUntil(this.destroy$));

    if (preRequest.awaitResponse === false) {
      this.showResultView(null);
      request$.subscribe({
        error: (err: unknown) => console.error('textarea-box: result pre-request failed', err),
      });
      return;
    }

    // Hold the card on the result view's own loading note rather than dropping
    // back to the textarea while the first service answers.
    this.resolvedResultView = null;
    this.resultError = '';
    this.resultLoading = true;
    this.resultMode = true;

    request$.subscribe({
      next: (res: any) => {
        if (preRequest.useResponseAsInitialRows === true) {
          this.showResultView(null, this.extractPreRequestRows(preRequest, res));
          return;
        }
        const payload = this.readPreRequestPayload(preRequest, res);
        if (!this.hasRequiredRecordLists(preRequest, payload)) {
          this.resultLoading = false;
          this.resultError = 'No matching price-file records were returned.';
          console.error('textarea-box: result request stopped because eList/evMap.list are empty');
          return;
        }
        this.showResultView(payload);
      },
      error: (err: unknown) => {
        console.error('textarea-box: result pre-request failed', err);
        if (preRequest.useResponseAsInitialRows === true || preRequest.recordListPayload?.requireNonEmpty) {
          // Stop here when the result depends entirely on this response.
          this.resultLoading = false;
          this.resultError = 'Unable to load matching price-file records.';
          return;
        }
        this.showResultView(null);
      },
    });
  }

  private buildPreRequestConfig(
    config: TextareaResultPreRequestConfig,
  ): TextareaResultPreRequestConfig {
    if (config.includeSelectedColumns !== true) {
      return config;
    }

    const columnsKey = String(this.savedSelection?.columnsKey || 'selected_columns').trim();
    if (!columnsKey) {
      return config;
    }

    const customPayload = JSON.parse(JSON.stringify(config.customPayload || {}));
    customPayload.requestData = {
      ...(customPayload.requestData || {}),
      [columnsKey]: this.columnSelectionValues,
    };

    return { ...config, customPayload };
  }

  /** Hands the widget its config, with `payload` merged into the requestData it sends. */
  private showResultView(
    payload: Record<string, unknown> | null,
    initialRows?: Record<string, unknown>[],
  ): void {
    const { preRequest: _preRequest, ...view } = this.resultView ?? {};

    this.resolvedResultView = {
      ...view,
      ...(payload
        ? { customPayload: { ...(view['customPayload'] as Record<string, unknown> | undefined), ...payload } }
        : {}),
      ...(initialRows !== undefined ? { initialRows } : {}),
    };
    this.resultLoading = false;
    this.resultMode = true;
    this.setSavedSelectionView(this.savedSelection?.resultViewState);
  }

  private setSavedSelectionView(value: unknown): void {
    const viewKey = String(this.savedSelection?.viewKey || '').trim();
    const normalizedValue = String(value || '').trim();
    if (viewKey && normalizedValue) {
      this.globalFilterService.updateFilterSelection(viewKey, [normalizedValue]);
    }
  }

  private extractPreRequestRows(
    config: TextareaResultPreRequestConfig,
    response: any,
  ): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return this.normalizePreRequestRows(response);
    }

    const entityKey = String(
      (config.customPayload as any)?.requestData?.entity || '',
    ).trim();
    const paths = [
      config.rowsPath,
      entityKey ? `responseList.0.${entityKey}` : '',
      entityKey ? `responseList.0.data.0.${entityKey}` : '',
      entityKey ? `data.${entityKey}` : '',
      entityKey,
      'tableRows',
      'rows',
      'data',
      'tableData',
      'responseList.0.data.0.tableRows',
      'responseList.0.data.0.rows',
      'responseList.0.data.0.data',
    ]
      .map((path) => String(path || '').trim())
      .filter(Boolean);

    for (const path of paths) {
      const rows = path
        .split('.')
        .reduce((current: any, part) => current?.[part], response);
      if (Array.isArray(rows)) {
        return this.normalizePreRequestRows(rows);
      }
    }

    return [];
  }

  /** NestedDynamic reads may wrap each entity result in `{ data: [...] }`. */
  private normalizePreRequestRows(rows: any[]): Record<string, unknown>[] {
    const nestedRows = rows.flatMap((entry) =>
      entry && typeof entry === 'object'
        ? Array.isArray(entry.data)
          ? entry.data
          : entry.data && typeof entry.data === 'object'
            ? [entry.data]
            : []
        : [],
    );
    const source = nestedRows.length ? nestedRows : rows;
    return source
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .map((row) =>
        row.data && typeof row.data === 'object' && !Array.isArray(row.data) && Object.keys(row).length === 1
          ? row.data
          : row,
      );
  }

  /**
   * What the pre-request is asked with: the named global-filter keys, or —
   * when none are configured — the loaded selection's filters and columns.
   */
  private buildPreRequestParams(config: TextareaResultPreRequestConfig): Record<string, unknown> {
    const keys = this.toList(config.filterKeys);
    const params = keys.length
      ? this.buildFilterSelections(keys, config.filterKeyMap)
      : this.buildSavedRequestParams();
    return this.normalizeScalarFilters(params, config.recordListPayload?.scalarFilterKeys);
  }

  /** The object to merge, honouring `responsePath`. Anything that is not a plain object is ignored. */
  private readPreRequestPayload(
    config: TextareaResultPreRequestConfig,
    response: any,
  ): Record<string, unknown> | null {
    const recordConfig = config.recordListPayload;
    if (recordConfig) {
      const configuredPath = String(recordConfig.rowsPath || '').trim();
      const sourceRows = [
        configuredPath,
        'tableRows',
        'responseList.0.data.0.tableRows',
        'data.0.tableRows',
      ]
        .filter(Boolean)
        .map((path) => path
          .split('.')
          .reduce((acc: any, part) => (acc == null ? acc : acc[part]), response))
        .find(Array.isArray);
      const filters = this.normalizeScalarFilters(
        this.buildSavedRequestParams(),
        recordConfig.scalarFilterKeys,
      );
      const columnarRecords = sourceRows
        ? []
        : this.buildColumnarRecords(response, filters, recordConfig);
      const records = this.filterResultRecords(sourceRows || columnarRecords, filters);
      const listKey = String(recordConfig.listKey || 'eList').trim();
      const mapKey = String(recordConfig.mapKey || 'evMap').trim();
      const mapListKey = String(recordConfig.mapListKey || 'list').trim();

      return {
        ...filters,
        [listKey]: records,
        [mapKey]: { [mapListKey]: records },
      };
    }

    const path = String(config.responsePath || '').trim();
    const value = path
      ? path.split('.').reduce((acc: any, part) => (acc == null ? acc : acc[part]), response)
      : response;

    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private normalizeScalarFilters(
    params: Record<string, unknown>,
    scalarKeys: string[] | undefined,
  ): Record<string, unknown> {
    const normalized = { ...params };
    (scalarKeys || []).forEach((key) => {
      const value = normalized[key];
      if (Array.isArray(value)) {
        const selected = value.flat(Infinity).find((item) => item !== null && item !== undefined && item !== '');
        normalized[key] = selected === undefined ? null : String(selected);
      }
    });
    return normalized;
  }

  /** Build row records from independent response-domain arrays. */
  private buildColumnarRecords(
    response: any,
    filters: Record<string, unknown>,
    config: NonNullable<TextareaResultPreRequestConfig['recordListPayload']>,
  ): Record<string, unknown>[] {
    const recordKey = String(config.recordKey || '').trim();
    if (!recordKey) return [];

    const candidates = [
      response,
      response?.responseList?.[0]?.data?.[0],
      response?.data?.[0],
    ];
    const source = candidates.find((candidate) =>
      candidate && typeof candidate === 'object' && Array.isArray(candidate[recordKey]));
    if (!source) return [];

    const fields = [...new Set([recordKey, ...(config.recordFields || [])])];
    return source[recordKey]
      .filter((value: unknown) => value !== null && value !== undefined && value !== '')
      .map((recordValue: unknown, index: number) => {
        const record: Record<string, unknown> = { [recordKey]: recordValue };
        fields.forEach((field) => {
          if (field === recordKey) return;

          const selected = this.firstScalarValue(filters[field]);
          if (selected !== undefined) {
            record[field] = selected;
            return;
          }

          // Only zip arrays that align with the driver. Short dropdown-domain
          // arrays describe options and are not record data.
          const domain = source[field];
          if (Array.isArray(domain) && domain.length === source[recordKey].length) {
            record[field] = domain[index];
          }
        });
        return record;
      });
  }

  private firstScalarValue(value: unknown): string | undefined {
    const values = Array.isArray(value) ? value.flat(Infinity) : [value];
    const selected = values.find((item) => item !== null && item !== undefined && item !== '');
    return selected === undefined ? undefined : String(selected);
  }

  /** Keep only rows matching saved filter values; response option arrays never participate. */
  private filterResultRecords(rows: any[], filters: Record<string, unknown>): Record<string, unknown>[] {
    return rows
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .filter((row) => Object.entries(filters).every(([key, selected]) => {
        if (!Object.prototype.hasOwnProperty.call(row, key)) return true;
        const selectedValues = (Array.isArray(selected) ? selected.flat(Infinity) : [selected])
          .filter((value) => value !== null && value !== undefined && value !== '')
          .map((value) => String(value));
        return !selectedValues.length || selectedValues.includes(String(row[key] ?? ''));
      }))
      .map((row) => ({ ...row }));
  }

  private hasRequiredRecordLists(
    config: TextareaResultPreRequestConfig,
    payload: Record<string, unknown> | null,
  ): boolean {
    const recordConfig = config.recordListPayload;
    if (!recordConfig?.requireNonEmpty) return true;
    if (!payload) return false;

    const listKey = String(recordConfig.listKey || 'eList').trim();
    const mapKey = String(recordConfig.mapKey || 'evMap').trim();
    const mapListKey = String(recordConfig.mapListKey || 'list').trim();
    const list = payload[listKey];
    const map = payload[mapKey] as Record<string, unknown> | undefined;
    return Array.isArray(list) && list.length > 0 && Array.isArray(map?.[mapListKey]) && map[mapListKey].length > 0;
  }

  private loadSavedItems(): void {
    const config = this.savedSelection;
    if (!config) {
      return;
    }

    // `items` is the fallback: it stands in until the service answers, and
    // stays put when the service is unreachable or returns nothing.
    const staticItems = this.normalizeSavedItems(config.items, config);
    const apiUrl = String(config.apiUrl || '').trim();
    if (!apiUrl) {
      this.setSavedItems(staticItems);
      return;
    }

    this.savedLoading = true;
    this.savedError = '';

    this.configService
      .getConfig(apiUrl, config, undefined, config.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.savedLoading = false;
          const items = this.resolveSavedItems(config, res);
          this.setSavedItems(items.length ? items : staticItems);
        },
        error: (err: unknown) => {
          this.savedLoading = false;
          this.setSavedItems(staticItems);
          if (!staticItems.length) {
            this.savedError = String(config.errorMessage ?? 'Unable to load saved selections.');
          }
          console.error('textarea-box: saved selections failed', err);
        },
      });
  }

  private setSavedItems(items: SavedSelectionItem[]): void {
    this.savedItems = items;
    const preferredName = this.preferredSavedItemName;
    this.activeSavedItem = (
      preferredName
        ? items.find((item) => item.name === preferredName)
        : undefined
    ) ?? items[0] ?? null;
    this.preferredSavedItemName = '';
    // A refreshed list replaces the very records an edit was working against.
    this.exitSavedEdit();
  }

  private resolveSavedItems(config: SavedSelectionConfig, response: any): SavedSelectionItem[] {
    const responseKey = String(config.responseDataKey || '').trim();
    const source = responseKey && response?.[responseKey] !== undefined
      ? response[responseKey]
      : Array.isArray(response?.data)
        ? response.data
        : response;

    return this.normalizeSavedItems(source, config);
  }

  private normalizeSavedItems(source: any, config: SavedSelectionConfig): SavedSelectionItem[] {
    if (!Array.isArray(source)) {
      return [];
    }

    const nameKey = String(config.nameKey || 'name');
    const columnsKey = String(config.columnsKey || 'columns');

    return source
      .map((entry: any) => ({
        name: String(entry?.[nameKey] ?? entry?.name ?? entry?.label ?? '').trim(),
        columns: this.toUniqueColumnValues(
          this.toList(entry?.[columnsKey] ?? entry?.columns ?? entry?.value),
        ),
        raw: entry,
      }))
      .filter((item: SavedSelectionItem) => !!item.name);
  }

  get showError(): boolean {
    return this.control.invalid && (this.control.dirty || this.control.touched);
  }

  get errorText(): string {
    const errors = this.control.errors;
    if (!errors) {
      return '';
    }

    const messages = this.data?.errorMessages || {};
    const key = Object.keys(errors)[0];

    if (messages[key]) {
      return messages[key];
    }

    switch (key) {
      case 'required':
        return `${this.labelText || 'This field'} is required.`;
      case 'minlength':
        return `Enter at least ${this.data?.minLength} characters.`;
      case 'maxlength':
        return `Enter no more than ${this.data?.maxLength} characters.`;
      case 'pattern':
        return 'Value is not in the expected format.';
      default:
        return 'Value is not valid.';
    }
  }

  confirmDialog(): void {
    this.control.markAllAsTouched();
    if (this.control.invalid) {
      return;
    }
    this.publish(this.control.value ?? '');
    this.dialogRef?.close(this.control.value ?? '');
  }

  cancelDialog(): void {
    this.dialogRef?.close();
  }

  private openDialog(): void {
    if (!this.dialogTemplate || this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(this.dialogTemplate, {
      width: this.data?.dialogWidth || '520px',
      autoFocus: false,
      restoreFocus: false,
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  // ------------------------------------------------------------------ wiring

  private buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.isRequired) {
      validators.push(Validators.required);
    }

    const min = Number(this.data?.minLength);
    if (Number.isFinite(min) && min > 0) {
      validators.push(Validators.minLength(min));
    }

    if (this.maxLength) {
      validators.push(Validators.maxLength(this.maxLength));
    }

    const pattern = String(this.data?.pattern || '').trim();
    if (pattern) {
      validators.push(Validators.pattern(pattern));
    }

    return validators;
  }

  private publish(value: string): void {
    const key = String(this.data?.key || '').trim();
    if (!key) {
      return;
    }
    const payloadValue = this.toPayloadValue(value);
    this.globalFilterService.updateFilterSelection(
      key,
      Array.isArray(payloadValue) ? payloadValue : [payloadValue],
      this.data?.isQueryParam,
    );
  }

  /** Re-joins with `payloadSeparator` when it differs from the display `separator`. */
  private toPayloadValue(value: string): string | string[] {
    const displaySeparator = String(this.data?.separator ?? ', ');
    const values = this.splitValue(value, displaySeparator);
    if (this.data?.payloadAsArray === true) {
      return this.toUniqueColumnValues(values);
    }

    const payloadSeparator = this.data?.payloadSeparator;
    if (payloadSeparator === undefined || payloadSeparator === displaySeparator) {
      return value;
    }
    return values.join(payloadSeparator);
  }

  private onFiltersChanged(filters: any): void {
    const changedKeys = this.toList(filters?.changedFilterKey);

    if (
      this.toolbarActionError
      && this.toolbarButtons.some((button) =>
        this.toList(button.requiredFilterKeys).some((key) => changedKeys.includes(key)))
    ) {
      this.toolbarActionError = '';
    }

    // Our own publish echoes back through filter$ — ignore it.
    const ownKey = String(this.data?.key || '').trim();
    if (ownKey && changedKeys.includes(ownKey)) {
      return;
    }

    // Once the result table is visible, changes to its declared filters rerun
    // the Load flow and replace the table's initial rows with the fresh result.
    const resultFilterKeys = this.toList(this.resultPreRequest?.filterKeys);
    if (
      this.resultMode
      && this.resultPreRequest?.reloadOnFilterChange !== false
      && resultFilterKeys.some((key) => changedKeys.includes(key))
    ) {
      this.openResultView();
    }

    const openOnKey = String(this.data?.openOnKey || '').trim();
    if (openOnKey && changedKeys.includes(openOnKey)) {
      this.openDialog();
    }

    const backKey = String(this.data?.backOnKey || '').trim();
    if (backKey && changedKeys.includes(backKey)) {
      this.stepBack();
    }

    // Saved-selection picker swaps the card body in place — no second dialog.
    const savedOpenKey = String(this.savedSelection?.openOnKey || '').trim();
    if (savedOpenKey && changedKeys.includes(savedOpenKey)) {
      this.openSavedSelection();
    }

    // The same key that leaves the picker also leaves the result view, so
    // Column Selection always brings the textarea back.
    const savedCloseKey = String(this.savedSelection?.closeOnKey || '').trim();
    if (savedCloseKey && changedKeys.includes(savedCloseKey)) {
      // Leave result mode before clearing dependent filters. Those clears emit
      // synchronously; if the table is still marked visible they start fresh
      // result requests whose late responses reopen the table.
      this.closeSavedSelection();
      this.resultMode = false;
      this.resultLoading = false;
      if (this.showCancelColumnEdit) {
        this.discardColumnSelectionEdit();
      } else {
        this.resetColumnSelectionWorkspace();
      }
    }

    if (this.isGlobalClear(filters, changedKeys)) {
      this.closeSavedSelection();
      this.resultMode = false;
      this.toolbarValue = this.toolbar?.select?.isMultiSelect === true ? [] : '';
      this.toolbarSearchText = '';
      this.syncFilteredToolbarOptions();
      this.control.setValue(this.seedValue, { emitEvent: false });
      this.control.markAsPristine();
      this.control.markAsUntouched();
      return;
    }

    const appendOnKeys = this.toList(this.data?.appendOnKeys);
    const sourceKeys = this.toList(this.data?.sourceKeys);

    if (!sourceKeys.length) {
      return;
    }

    if (appendOnKeys.length) {
      // Commit-style: only pull from the sources when the trigger fires.
      if (appendOnKeys.some((key) => changedKeys.includes(key))) {
        this.applyValues(this.readSources(filters, sourceKeys), true);
      }
      return;
    }

    // Mirror-style: track the sources as they change.
    if (sourceKeys.some((key) => changedKeys.includes(key))) {
      this.applyValues(this.readSources(filters, sourceKeys), false);
    }
  }

  private isGlobalClear(filters: any, changedKeys: string[]): boolean {
    if (this.data?.resetOnClearAll === false || changedKeys.length) {
      return false;
    }

    // clearAllFilters() marks its emission explicitly. Prefer that signal over
    // inspecting the bags: a default toggle button can synchronously restore
    // its view-state key while this same reset emission is still being handled,
    // making an otherwise global clear look non-empty to later subscribers.
    if (filters?.changedFilterKey === null) {
      return true;
    }

    const isEmpty = (bag: any) => !bag || Object.keys(bag).length === 0;
    return isEmpty(filters?.queryParams) && isEmpty(filters?.requestParams);
  }

  /** Snapshot of other widgets' current selections, keyed as configured (used to build a nested `filter` payload). */
  private buildFilterSelections(
    keys: string[],
    keyMap: Record<string, string> = {},
  ): Record<string, unknown> {
    const current = this.globalFilterService.getCurrentFilters();
    const bag = { ...(current?.queryParams || {}), ...(current?.requestParams || {}) };

    const filter: Record<string, unknown> = {};
    keys.forEach((key) => {
      const value = bag[key];
      const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
      if (hasValue) {
        filter[keyMap[key] || key] = value;
      }
    });
    return filter;
  }

  private readSources(filters: any, sourceKeys: string[]): string[] {
    const values: string[] = [];

    sourceKeys.forEach((key) => {
      const raw = filters?.queryParams?.[key] ?? filters?.requestParams?.[key];
      this.toList(raw).forEach((value) => values.push(value));
    });

    return values;
  }

  private applyValues(values: string[], append: boolean): void {
    if (!values.length) {
      return;
    }

    const separator = this.data?.separator ?? ', ';
    const existing = append ? this.splitValue(this.control.value ?? '', separator) : [];
    const merged = [...existing, ...values];

    this.setColumnSelectionValues(this.toUniqueValues(merged));
  }

  private setColumnSelectionValues(values: string[]): void {
    const separator = this.data?.separator ?? ', ';
    const normalized = this.toPayloadList(values);
    if (this.savedEditMode) {
      this.savedEditColumns = [...normalized];
    }
    this.control.setValue(normalized.join(separator));
    this.control.markAsDirty();
  }

  /** Collapses repeats unless the config asks for them. */
  private toUniqueValues(values: string[]): string[] {
    return this.data?.allowDuplicates === true ? values : [...new Set(values)];
  }

  /** Produces the canonical backend column list for saved-selection Load. */
  private toPayloadList(values: string[]): string[] {
    return this.toUniqueColumnValues(values);
  }

  /** Split legacy CSV entries and remove repeats without changing column order. */
  private toUniqueColumnValues(values: string[]): string[] {
    return [...new Set(
      values
        .flatMap((value) => String(value ?? '').split(/[\n,]/))
        // Some APIs persist the array as a JSON-like string. Splitting that
        // value used to leave `[` on the first column and `]` on the last.
        .map((value) => value
          .trim()
          .replace(/^\[\s*/, '')
          .replace(/\s*\]$/, '')
          .replace(/^["']|["']$/g, '')
          .trim())
        .filter(Boolean),
    )];
  }

  private splitValue(value: string, separator: string): string[] {
    const token = separator.trim() || separator;
    return value
      .split(token)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  private loadToolbarOptions(): void {
    const select = this.toolbar?.select;
    if (!select) {
      return;
    }

    if (Array.isArray(select.options) && select.options.length) {
      this.toolbarOptions = this.excludeToolbarOptions(
        select.options.map((opt) => ({ label: opt, value: opt })),
        select,
      );
      this.syncFilteredToolbarOptions();
      return;
    }

    const apiUrl = String(select.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    this.configService
      .getConfig(apiUrl, select, undefined, select.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.toolbarOptions = this.resolveToolbarOptions(select, res);
          this.syncFilteredToolbarOptions();
        },
        error: (err: unknown) => console.error('textarea-box: toolbar options failed', err),
      });
  }

  /** Same response contract as global-dropdown, plus an optional nested-path + label/value split. */
  private resolveToolbarOptions(select: TextareaToolbarSelect, response: any): TextareaToolbarOption[] {
    const optionsPath = String(select.optionsPath || '').trim();
    let source: unknown;

    if (optionsPath) {
      source = this.resolvePath(response, optionsPath);
    } else {
      const responseKey = String(select.responseDataKey || select.key || '').trim();
      source = Array.isArray(response?.data)
        ? response.data
        : responseKey && response?.[responseKey] !== undefined
          ? response[responseKey]
          : Array.isArray(response)
            ? response
            : [];
    }

    if (!Array.isArray(source)) {
      return [];
    }

    const labelKey = String(select.optionLabelKey || '').trim();
    const valueKey = String(select.optionValueKey || '').trim();

    if (labelKey || valueKey) {
      const seen = new Set<string>();
      const options: TextareaToolbarOption[] = [];
      this.expandToolbarAttributeOptions(source as any[]).forEach((entry) => {
        // Attribute APIs may return either metadata objects or plain column
        // strings. Falling back to the entry itself prevents a blank ng-select
        // value when optionLabelKey/optionValueKey are configured for objects.
        const isObject = entry && typeof entry === 'object' && !Array.isArray(entry);
        const rawValue = isObject
          ? entry[valueKey || labelKey] ?? entry.name ?? entry.displayName
          : entry;
        const rawLabel = isObject
          ? entry[labelKey || valueKey] ?? entry.displayName ?? entry.name ?? rawValue
          : entry;
        const value = String(rawValue ?? '').trim();
        const label = String(rawLabel ?? value).trim();
        if (value && label && !seen.has(value)) {
          seen.add(value);
          options.push({ label, value });
        }
      });
      return this.excludeToolbarOptions(options, select);
    }

    // Legacy flat contract: options are plain strings (or CSV-encoded strings), label === value.
    const flat = (source as unknown[])
      .flat(Infinity)
      .flatMap((value: unknown) => (typeof value === 'string' ? value.split(',') : [value]))
      .filter((value: unknown) => value !== null && value !== undefined)
      .map((value: unknown) => String(value).trim())
      .filter(Boolean);

    return this.excludeToolbarOptions(
      [...new Set(flat)].map((value) => ({ label: value, value })),
      select,
    );
  }

  /**
   * JSONB array attributes are containers, not selectable table columns. Expose
   * their leaf metadata instead, while leaving every other attribute untouched.
   */
  private expandToolbarAttributeOptions(source: any[]): any[] {
    return source.flatMap((entry) => {
      const isJsonArray = entry
        && typeof entry === 'object'
        && !Array.isArray(entry)
        && String(entry.type || '').trim().toLowerCase() === 'array'
        && String(entry.subType || '').trim().toLowerCase() === 'json'
        && Array.isArray(entry.subFields);

      return isJsonArray ? entry.subFields : [entry];
    });
  }

  private excludeToolbarOptions(
    options: TextareaToolbarOption[],
    select: TextareaToolbarSelect,
  ): TextareaToolbarOption[] {
    const excluded = new Set(
      this.toList(select.excludeOptions).map((value) => this.normalizeOptionIdentity(value)),
    );
    if (!excluded.size) return options;
    return options.filter((option) =>
      !excluded.has(this.normalizeOptionIdentity(option.label)) &&
      !excluded.has(this.normalizeOptionIdentity(option.value))
    );
  }

  private normalizeOptionIdentity(value: unknown): string {
    return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private syncFilteredToolbarOptions(): void {
    const search = this.toolbarSearchText.trim().toLowerCase();
    this.filteredToolbarOptions = search
      ? this.toolbarOptions.filter((option) =>
          option.label.toLowerCase().includes(search) || option.value.toLowerCase().includes(search)
        )
      : [...this.toolbarOptions];
  }

  private resolvePath(source: any, path: string): unknown {
    return path
      .split('.')
      .reduce((acc: any, key: string) => (acc === null || acc === undefined ? undefined : acc[key]), source);
  }

  private toList(value: any): string[] {
    if (value === null || value === undefined) {
      return [];
    }
    const source = Array.isArray(value) ? value : [value];
    return source
      .flat(Infinity)
      .filter((item) => item !== null && item !== undefined)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }
}
